// Small, failure-tolerant wrappers around localStorage. Private windows and blocked storage just
// mean nothing is remembered; the app keeps working.

function read<T>(key: string, fallback: T, store: 'local' | 'session' = 'local'): T {
  try {
    const raw = (store === 'local' ? localStorage : sessionStorage).getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown, store: 'local' | 'session' = 'local'): void {
  try {
    const s = store === 'local' ? localStorage : sessionStorage;
    if (value === null || value === undefined) s.removeItem(key);
    else s.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable.
  }
}

export interface Seat {
  token: string;
  playerId: string;
  savedAt: number;
}

const SEATS = 'cm:seats';

export function getSeat(code: string): Seat | null {
  return read<Record<string, Seat>>(SEATS, {})[code] ?? null;
}

export function saveSeat(code: string, seat: Omit<Seat, 'savedAt'>): void {
  const seats = read<Record<string, Seat>>(SEATS, {});
  seats[code] = { ...seat, savedAt: Date.now() };
  // Keep the ten most recent rooms.
  const recent = Object.entries(seats).sort((a, b) => b[1].savedAt - a[1].savedAt).slice(0, 10);
  write(SEATS, Object.fromEntries(recent));
}

export function forgetSeat(code: string): void {
  const seats = read<Record<string, Seat>>(SEATS, {});
  delete seats[code];
  write(SEATS, seats);
}

export function getName(): string {
  return read('cm:name', '');
}

export function saveName(name: string): void {
  write('cm:name', name);
}

export function getAvatar(): string {
  return read('cm:avatar', '');
}

export function saveAvatar(avatar: string): void {
  write('cm:avatar', avatar);
}

export function getShowGloss(): boolean {
  return read('cm:gloss', true);
}

export function saveShowGloss(on: boolean): void {
  write('cm:gloss', on);
}

export function getShowDevanagari(): boolean {
  return read('cm:deva', false);
}

export function saveShowDevanagari(on: boolean): void {
  write('cm:deva', on);
}

export interface MyPack {
  code: string;
  editKey: string;
  name: string;
}

export function getMyPacks(): MyPack[] {
  return read<MyPack[]>('cm:packs', []);
}

export function saveMyPack(pack: MyPack): void {
  write('cm:packs', [pack, ...getMyPacks().filter((p) => p.code !== pack.code)].slice(0, 30));
}

export function forgetMyPack(code: string): void {
  write('cm:packs', getMyPacks().filter((p) => p.code !== code));
}

export function getAdminToken(): string {
  return read('cm:admin', '', 'session');
}

export function saveAdminToken(token: string | null): void {
  write('cm:admin', token, 'session');
}

/** Anonymous id for this browser, used only for first-party analytics. */
export function getDeviceId(): string {
  let id = read<string>('cm:device', '');
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    write('cm:device', id);
  }
  return id;
}
